import { useState } from "react";
import { RecordCategoryDto, recordCategoryFormData, recordCaterogryIdKey } from "../types/recordCategory";
import { recordCategoryService } from "../services/recordCategoryService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const useRecordCategories = () => {
  // 1. Το GET γίνεται αυτόματα με το useApi, cache και signal
  const {
    data: recordCategories = [],
    loading: isFetching,
    error: getError,
    refetch: fetchRecordCategories,
    invalidateCache,
  } = useApi<RecordCategoryDto[]>("record-categories", (signal) => recordCategoryService.getAll({}, signal));

  // 2. Το CREATE τυλίγεται στο καθαρό useMutation
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newCategory: typeof recordCategoryFormData) => recordCategoryService.create(newCategory));

  // 3. Το UPDATE τυλίγεται στο καθαρό useMutation
  const {
    execute: updateExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(({ id, data }: { id: number | string; data: RecordCategoryDto }) =>
    recordCategoryService.update(id, data)
  );

  // 4. Το DELETE τυλίγεται στο καθαρό useMutation
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => recordCategoryService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createRecordCategory = async (newCategory:typeof recordCategoryFormData): Promise<void> => {
    try {
      await createExecution(newCategory);
      
      // Καθαρισμός cache για να φέρει τα νέα δεδομένα στην επόμενη προβολή
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η κατηγορία αρχείου αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      
      fetchRecordCategories(true); // Άμεση ανανέωση λίστας
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const updateRecordCategory = async (id: number | string, updatedData: RecordCategoryDto): Promise<void> => {
    try {
      await updateExecution({ id, data: updatedData });
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      fetchRecordCategories(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deleteRecordCategories = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      
      // Καθαρισμός cache
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      
      fetchRecordCategories(true); // Άμεση ανανέωση λίστας
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    recordCategories,
    isLoading: isFetching || isCreating || isUpdating || isDeleting,
    error: getError || createError || updateError || deleteError,
    fetchRecordCategories: () => fetchRecordCategories(true),
    createRecordCategory,
    updateRecordCategory,
    deleteRecordCategories,
    modalState,
    closeModal,
  };
};